<template lang="pug">
.pdf-mobile
  .pdf-mobile__container
    pdf-document.pdf-render(
      :class='{ loading: loading }'
      :scale='effectiveScale'
      :src='src'
      @proxy-pages='onProxyPages'
    )
      .pdf-render__loading.d-flex.justify-content-center(v-if='loading')
        span.text.align-self-center Carregando documento...
      .pdf-render__content.d-flex
        .pdf-render__content__pages(
          @scroll='onScrollPages'
          @touchend='onTouchEnd'
          @touchmove='onTouchMove'
          @touchstart='onTouchStart'
        )
          template(v-for='proxyPage in proxyPages')
            pdf-page-mobile.pdf-page(
              :key='`page-${proxyPage.pageNumber}-${version}`'
              :proxy-page='proxyPage'
              :scale='currentScale'
            )
              slot(:name='`page-${proxyPage.pageNumber}`')
    .pdf-mobile__controls.p-2.m-2.d-flex.justify-content-between.align-items-center
      .pdf-mobile__controls__navigation
        button.pdf-mobile__controls__button(
          :disabled='selectedPage === pages.length'
          @click='goScrollToPage(selectedPage + 1)'
          v-tooltip:bottom='`Próxima Página`'
        )
          icon(name='arrow-down')
        span.pdf-mobile__controls__page-counter {{ selectedPage }} / {{ pages.length }}
        button.pdf-mobile__controls__button(
          :disabled='selectedPage === 1'
          @click='goScrollToPage(selectedPage - 1)'
          v-tooltip:bottom='`Página Anterior`'
        )
          icon(name='arrow-up')
      .pdf-mobile__controls__zoom
        pdf-zoom-controls(
          :current-scale.sync='currentScale'
          :max-zoom='maxZoom'
          :min-zoom='minZoom'
        )
</template>

<script>
import debounce from 'debounce';
import Icon from '@/ui/atoms/Icon.vue';
import PDFDocument from './PDFDocument.vue';
import PDFPageMobile from './PDFPageMobile.vue';
import PdfZoomControls from './PdfZoomControls.vue';

export default {
  name: 'PdfMobile',
  components: {
    Icon,
    'pdf-document': PDFDocument,
    'pdf-page-mobile': PDFPageMobile,
    PdfZoomControls,
  },
  props: {
    src: {
      type: String,
      required: true,
    },
    showEnvelopeOptions: {
      type: Boolean,
      default: false,
    },
    showExpandOptions: {
      type: Boolean,
      default: false,
    },
    scale: {
      type: Number,
      default: 1.0, //100%
    },
    mobileScale: {
      type: Number,
      default: 1.0, //100%
    },
    maxZoom: {
      type: Number,
      default: 1.2, //120%
    },
    minZoom: {
      type: Number,
      default: 0.6, //60%
    },
  },
  data: () => ({
    loading: true,
    proxyPages: [],
    pages: [],
    selectedPage: 1,
    containerSize: 0,
    version: 1,
    currentPage: 0,
    numPages: '',
    currentScale: 0.6,
    initialTouchDistance: 0,
    initialScale: 1.0,
    lastTouchDistance: 0,
    isPinching: false,
    isMounted: false,
  }),
  mounted() {
    this.$nextTick(() => {
      this.updateHeight();
      const resizeObserver = new ResizeObserver((entries) => {
        entries.forEach((e) => {
          this.updateContainerSize(e);
        });
      });

      const pagesEl = document.querySelector('.pdf-render__content__pages');
      if (pagesEl) {
        resizeObserver.observe(pagesEl);
      }

      this.updateScale();
      this.updatePdfScale();
      this.isMounted = true;
    });
  },
  methods: {
    onProxyPages(proxyPages) {
      this.proxyPages = proxyPages;
      this.pages = this.proxyPages.map((pp) => ({
        pageNumber: pp.pageNumber,
        elementSize: {},
        pageSize: {},
      }));
      this.$emit('document-loaded', true);
      this.$emit('proxy-pages', this.proxyPages);
      this.loading = false;
    },
    onScrollPages(e) {
      this.pages.forEach((page) => {
        const el = document.querySelector(`#page-${page.pageNumber}`);
        if (e.target.scrollTop > el.offsetTop - el.clientHeight / 2) {
          this.selectedPage = page.pageNumber;
          this.$emit('selected-page', this.selectedPage);
        }
      });
    },
    onTouchStart(e) {
      if (e.touches.length === 2) {
        this.isPinching = true;
        this.initialTouchDistance = this.getTouchDistance(e.touches);
        this.initialScale = this.currentScale;
      }
    },
    onTouchMove(e) {
      if (this.isPinching && e.touches.length === 2) {
        e.preventDefault();
        const currentDistance = this.getTouchDistance(e.touches);
        const scale =
          (currentDistance / this.initialTouchDistance) * this.initialScale;

        if (scale >= this.minZoom && scale <= this.maxZoom) {
          this.currentScale = scale;
          this.updatePdfScale();
        }
      }
    },
    onTouchEnd() {
      this.isPinching = false;
      this.initialTouchDistance = 0;
      this.initialScale = this.currentScale;
    },
    getTouchDistance(touches) {
      const dx = touches[0].clientX - touches[1].clientX;
      const dy = touches[0].clientY - touches[1].clientY;
      return Math.sqrt(dx * dx + dy * dy);
    },
    goScrollToPage(pageNumber) {
      const pagesEl = document.querySelector('.pdf-render__content__pages');
      const pageEl = document.querySelector(`#page-${pageNumber}`);
      pagesEl.scrollTop = pageEl.offsetTop - 80;
      this.$emit('selected-page', pageNumber);
    },
    updateContainerSize: debounce(function updateContainerSize(e) {
      if (this.containerSize !== e.contentRect.width) {
        this.updateHeight();
        this.containerSize = e.contentRect.width;
        this.version += 1;
      }
    }, 800),
    updateHeight() {
      const pagesEl = document.querySelector('.pdf-render__content__pages');
      if (!pagesEl) return;

      const width = pagesEl.clientWidth;
      pagesEl.style.height = `${width * 1.5}px`;
    },
    updateScale() {
      this.currentScale = this.$root.isMobile
        ? this.mobileScale ?? 1.0
        : this.scale ?? 1.0;
    },
    updatePdfScale: debounce(function updatePdfScale() {
      if (!this.isMounted) return;

      this.$emit('scale-change', this.currentScale);

      const pagesEl = document.querySelector('.pdf-render__content__pages');
      if (!pagesEl) return;

      this.updateHeight();
      this.$emit('version', this.version);
      this.$emit('size', {
        width: this.$el.clientWidth,
        height: this.$el.clientHeight,
      });
      this.version += 1;
    }, 100),
  },
  computed: {
    effectiveScale() {
      return this.currentScale;
    },
  },
  watch: {
    currentScale: {
      handler() {
        if (!this.isMounted) return;
        this.updatePdfScale();
      },
      immediate: false,
    },
  },
};
</script>

<style lang="stylus">
.pdf-mobile
  position: relative
  width: 100%
  height: 100%
  overflow: hidden
  background: #f5f5f5

  &__container
    position: relative
    width: 100%
    height: 100%
    overflow-y: auto
    overflow-x: auto
    -webkit-overflow-scrolling: touch
    scroll-behavior: smooth
    white-space: nowrap

  .pdf-render
    width: 100%
    height: 100%

    &__content
      width: 100%
      height: 100%
      position: relative

      &__pages
        width: max-content
        min-width: 100%
        height: 100%
        overflow-y: auto
        overflow-x: auto
        -webkit-overflow-scrolling: touch
        scroll-behavior: smooth
        padding: 16px
        box-sizing: border-box
        touch-action: pan-x pan-y pinch-zoom

        &::-webkit-scrollbar {
          height: 10px;
          background: #e0e0e0;
        }
        &::-webkit-scrollbar-thumb {
          background: #013D52;
          border-radius: 8px;
        }
        &::-webkit-scrollbar-corner {
          background: #e0e0e0;
        }
        scrollbar-width: thin;
        scrollbar-color: #013D52 #e0e0e0;

        .pdf-page
          position: relative
          margin: 0 16px 16px 0
          background: white
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1)
          border-radius: 8px
          overflow: visible
          transform-origin: top left

          canvas
            display: block
            transform-origin: top left
            transition: transform 0.2s ease-out
            image-rendering: -webkit-optimize-contrast
            image-rendering: crisp-edges

          .document-element-viewer
            position: absolute
            top: 0
            left: 0
            width: 100%
            height: 100%
            pointer-events: none

            .document-element
              position: absolute
              pointer-events: auto
              transition: transform 0.2s ease-out

  &__controls
    position: relative
    display: flex
    justify-content: space-between
    align-items: center
    padding: 16px
    background: rgba(255, 255, 255, 0.9)
    backdrop-filter: blur(10px)
    box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.1)
    margin-top: 16px
    border-radius: 8px

    &__navigation
      display: flex
      gap: 16px
      align-items: center

    &__page-counter
      font-size: 0.9rem
      font-weight: 500
      color: #013D52
      min-width: 60px
      text-align: center

    &__button
      background: none
      border: none
      padding: 8px
      cursor: pointer
      color: #013D52
      transition: all 0.2s ease
      border-radius: 50%
      display: flex
      align-items: center
      justify-content: center
      width: 40px
      height: 40px

      &:hover:not(:disabled)
        background: rgba(1, 61, 82, 0.1)

      &:disabled
        opacity: 0.6
        cursor: not-allowed

      .icon
        font-size: 1.2rem

@media (max-width: 576px)
  .pdf-mobile
    .pdf-render
      &__content
        &__pages
          padding: 8px

          .pdf-page
            margin-bottom: 8px
            border-radius: 4px

            canvas
              transform-origin: top left !important

            .document-element-viewer
              .document-element
                transform-origin: top left !important

    &__controls
      padding: 12px
      margin-top: 12px

      &__navigation
        gap: 12px

      &__page-counter
        font-size: 0.8rem
        min-width: 50px

      &__button
        width: 36px
        height: 36px
        padding: 6px

        .icon
          font-size: 1rem

@media (max-width: 375px)
  .pdf-mobile
    .pdf-render
      &__content
        &__pages
          padding: 4px

          .pdf-page
            margin-bottom: 4px
            border-radius: 2px

    &__controls
      padding: 8px
      margin-top: 8px

      &__navigation
        gap: 8px

      &__page-counter
        font-size: 0.75rem
        min-width: 45px

      &__button
        width: 32px
        height: 32px
        padding: 4px

        .icon
          font-size: 0.9rem
</style>
